import React, { useContext } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import clsx from 'clsx';
import { ExternalManipulationFilterDTO, ExternalManipulationUnitDTO, PageResponseExternalManipulationUnitDTO, PermissionTypesEnum } from '@api/ringil-api.v2.tsx';
import { Button } from 'primereact/button';
import { useMediaQuery } from 'react-responsive';
import styled from 'styled-components';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import COHeadline from '@components/framework/headlines/COHeadline';
import { InputText } from 'primereact/inputtext';
import { FilterPage, FilterSort, useFilter } from '@hooks/useFilter/Filter.tsx';
import { ApiContext } from '@api/api.tsx';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import SvgEdit from '@icons/pencil-inverted.svg?react';
import SvgDelete from '@icons/delete-trash.svg?react';
import SvgPlus from '@icons/plus.svg?react';
import { useVolumetricItemSidebar } from '@app/pages/settings/VolumetricItemSidebar.tsx';
import { exportExcel } from '@utils/exports.ts';

interface ComponentProps {}

const Volumetric: React.FC<ComponentProps> = () => {
  const { tr } = useTranslationLgs();
  const { hasPermission } = useContext(AuthenticatedUserContext);
  const isMobile = useMediaQuery({ maxWidth: 1024 });

  const { listExternalManipulationUnits, deleteExternalManipulationUnit } = useContext(ApiContext);

  const [data, setData] = React.useState<PageResponseExternalManipulationUnitDTO>(null);

  const { VolumetricItemSidebar, setVisible: setVibildeVolumetricSidebar } = useVolumetricItemSidebar();

  const [rowToEdit, setRowToEdit] = React.useState<ExternalManipulationUnitDTO>(null);

  const { applyFilterValue, filter, applyPaging, page, onPage, applySorting, sort, applyFilter } = useFilter<ExternalManipulationFilterDTO>({
    onLoad: (page: FilterPage, filter: ExternalManipulationFilterDTO) => {
      loadData(page, filter, sort);
    },
    filterName: 'volumetric_list',
    defaultSort: { sortField: 'externalId', sortDirection: 'ASC' },
  });

  const loadData = (page: FilterPage, filter: ExternalManipulationFilterDTO, sort: FilterSort) => {
    listExternalManipulationUnits(
      {
        pageSize: page.pageSize,
        pageNumber: page.pageNumber,
        sortField: sort.sortField,
        sortDirection: sort.sortDirection,
      },
      filter,
      d => {
        setData(d);
      },
    );
  };

  const renderActions = (row: ExternalManipulationUnitDTO) => (
    <div className="flex flex-row gap-2">
      <RingilButton
        onClick={() => {
          setRowToEdit(row);
          setVibildeVolumetricSidebar(true);
        }}
        icon={<SvgEdit />}
        mode={'table'}
      />
      <RingilButton
        onClick={() => {
          deleteExternalManipulationUnit(row.id, () => {
            loadData(page, filter, sort);
          });
        }}
        icon={<SvgDelete />}
        mode={'table'}
      />
    </div>
  );

  const exportExcelHandler = () => {
    listExternalManipulationUnits(
      {
        pageSize: 9999999,
        pageNumber: 1,
        sortField: sort.sortField,
        sortDirection: sort.sortDirection,
      },
      {},
      (importData: PageResponseExternalManipulationUnitDTO) => {
        const excelData = importData.data.map(data => ({
          externalId: data.externalId,
          name: data.name,
          unitWeight: data.unitWeight,
          unitsOnPallet: data.unitsOnPallet,
          unitsInBox: data.unitsInBox,
          boxOnPallet: data.boxOnPallet,
          boxWeight: data.boxWeight,
          palletWeight: data.palletWeight,
          cargoTemplateCode: data.cargoTemplateName,
        }));
        exportExcel(excelData, 'volumetric');
      },
    );
  };

  return (
    <>
      <div className={clsx('r-legacy-list-fields', "font-['DM Sans']", isMobile && '-mx-4 px-8 pt-8')}>
        {!isMobile && <BreadCrumbComponent items={[{ label: tr(`Volumetric.breadCrumb`, `Volumetric`) }]} />}
        <div className="flex items-center mb-6">
          <COHeadline title={tr(`Volumetric.pageTitle`, `Volumetric`)} variant={'h1'} />
          <div>
            {hasPermission([PermissionTypesEnum.PERMISSION_EXPEDITION_CREATE]) && (
              <div className="flex items-center">
                <RingilButton label={tr(`Volumetric.newItem`, `New item`)} icon={<SvgPlus />} mode={'text-only'} onClick={() => setVibildeVolumetricSidebar(true)} />
              </div>
            )}
          </div>
        </div>
        <div className="filters">
          <FilterRow>
            <div className="primary-filters">
              <div className="field" key="externalId">
                <InputText
                  value={filter?.externalId || ''}
                  onChange={e => {
                    const { value } = e.target as HTMLInputElement;
                    applyFilterValue('externalId', value);
                  }}
                  placeholder={tr(`Volumetric.filterExternalId`, `External id`)}
                  className="w-full"
                />
              </div>
              <div className="field" key="name">
                <InputText
                  value={filter?.name || ''}
                  onChange={e => {
                    const { value } = e.target as HTMLInputElement;
                    applyFilterValue('name', value);
                  }}
                  placeholder={tr(`Volumetric.filterName`, `Name`)}
                  className="w-full"
                />
              </div>
            </div>
          </FilterRow>
        </div>

        <TableWrapper>
          {data?.data && (
            <>
              <div className="mt-4 bg-[#E7EBEE] p-4">
                <div className="flex justify-end items-center">
                  <Button
                    type="button"
                    icon="pi pi-file"
                    label={tr('Volumetric.exportCSV', 'Export to CSV')}
                    onClick={exportExcelHandler}
                    className="p-button-text !text-orange-500 hover:!text-orange-600 !inline-flex !items-center"
                  />
                </div>
              </div>
              <DataTable
                value={data.data}
                rows={data.size}
                paginator={true}
                lazy={true}
                totalRecords={data.total}
                onPage={onPage}
                first={(data.page - 1) * data.size}
                sortMode="single"
                removableSort={false}
                onSort={e => {
                  applySorting(e.sortField, e.sortOrder === 1 ? 'ASC' : 'DESC');
                }}
                sortField={sort.sortField}
                sortOrder={sort.sortDirection === 'ASC' ? 1 : -1}
                className="ringil-table"
                rowsPerPageOptions={[10, 25, 50, 100]}
              >
                <Column sortable={true} sortField={'externalId'} header={tr(`Volumetric.columnExternalId`, `External id`)} body={row => row.externalId} />
                <Column sortable={true} sortField={'name'} header={tr(`Volumetric.columnName`, `Name`)} body={row => row.name} />
                <Column header={tr(`Volumetric.columnUnitWeight`, `Unit weight`)} body={row => row.unitWeight} />
                <Column header={tr(`Volumetric.columnUnitsOnPallet`, `Units on pallet`)} body={row => row.unitsOnPallet} />
                <Column header={tr(`Volumetric.columnUnitsInBox`, `Units in box`)} body={row => row.unitsInBox} />
                <Column header={tr(`Volumetric.columnBoxWeight`, `Box weight`)} body={row => row.boxWeight} />
                <Column header={tr(`Volumetric.columnPalletWeight`, `Pallet weight`)} body={row => row.palletWeight} />
                <Column header={tr(`Volumetric.columnCargoTemplateName`, `Cargo template name`)} body={row => row.cargoTemplateName} />
                <Column body={renderActions} />
              </DataTable>
            </>
          )}
        </TableWrapper>

        <VolumetricItemSidebar
          onContinue={() => {
            setRowToEdit(null);
            loadData(page, filter, sort);
          }}
          item={rowToEdit}
        />
      </div>
    </>
  );
};

export default Volumetric;


const FilterRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 1rem 0;
  font-family: 'DM Sans', sans-serif;

  .primary-filters,
  .additional-filters {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.5rem 1rem;
    width: 100%;

    @media (max-width: 1280px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 768px) {
      grid-template-columns: repeat(1, minmax(0, 1fr));
    }

    &.hidden {
      display: none;
    }

    .field {
      min-width: 200px;

      @media (max-width: 768px) {
        width: 100%;
        min-width: 100%;
      }

      .p-inputtext,
      .p-calendar,
      .p-multiselect,
      .p-dropdown {
        width: 100%;
      }
    }

    .field-checkbox {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .dates-group {
      display: flex;
      gap: 0.5rem;
      grid-column: span 1;

      @media (max-width: 1280px) {
        grid-column: span 1;
      }

      @media (max-width: 768px) {
        grid-column: span 1;
        flex-direction: row;
      }

      .field {
        min-width: unset;
        flex: 1;
      }
    }
  }
`;

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  font-family: 'DM Sans', sans-serif;
  margin-top: 3px;
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-right: 16px;
  background: none;
  border: none;
  font-weight: 400;
  cursor: pointer;
  font-family: inherit;
  font-size: 1rem;
  color: #fe5000;
  padding: 0;
  transition: opacity 0.2s ease-in-out;

  &:hover {
    opacity: 0.8;
  }
`;
